"use client";

import { c as _c } from "react/compiler-runtime";
import { isLiveVoice } from "@workspace/voice-shared/live";
import type { LiveCallPhase } from "@workspace/voice-shared/live-call";
import { useEffect, useMemo } from "react";
import { useMagnusClient } from "../provider";
import { useLatest } from "../use-latest";
import type { LiveCallChatBridge } from "./bridge";
import { createMagnusLiveCall, type LiveCallAvailability, type MagnusLiveCallController, type MagnusLiveCallOptions } from "./controller";
import type { AudioInputDevice, LiveCallStartResult, LiveCaptions } from "./store";
import { type LiveCallCalmState, type LiveCallSubscribable, useLiveCallState } from "./use-live-call-state";

/** Prop-ready source for the magnus-chat block's `liveCall` prop: a flat
 *  snapshot, rebuilt whenever any field but the meter levels changes. */
export interface MagnusLiveCallSource {
  callChatId: string | null;
  captions: LiveCaptions;
  /** Whether the pill shows captions. */
  captionsOn: boolean;
  delegatingQuery: string | null;
  error: string | null;
  /** With no call bound, the chat `error` belongs in; null for any chat. */
  errorChatId: string | null;
  inputDeviceId: string | null;
  inputDevices: AudioInputDevice[];
  isMuted: boolean;
  /** The levels, which tick every ~60 ms in a call. Read them with
   *  `useLiveCallLevels(meter)` in the one component that draws them, so
   *  the rest of the chat doesn't re-render on each tick. */
  meter: LiveCallSubscribable;
  phase: LiveCallPhase;
  pushContext: (notes: readonly string[]) => void;
  registerChat: (bridge: LiveCallChatBridge) => () => void;
  sessionId: string | null;
  setCaptionsOn: (on: boolean) => void;
  setInputDevice: (deviceId: string | null) => Promise<void>;
  setMuted: (muted: boolean) => void;
  /** Ignores ids not in `voices`. */
  setVoice: (voice: string) => void;
  start: () => Promise<LiveCallStartResult>;
  startedAt: string | null;
  stop: () => void;
  voice: string;
  voices: readonly {
    id: string;
    label: string;
  }[];
}
export interface UseMagnusLiveCallOptions extends Pick<MagnusLiveCallOptions, "openChat" | "onChatMaterialized" | "onRecapSaved"> {
  /** Default true. False keeps the source absent (no call button) and
   *  doesn't subscribe, so availability isn't fetched. */
  enabled?: boolean;
}
const noSubscription = () => () => undefined;

/**
 * The block's `liveCall` source for `state`. `error` defaults to
 * `state.error`, which includes a refused start and a failed save; a host
 * that toasts those instead passes `state.call.error` so each failure has
 * one surface.
 */
export function toLiveCallSource(controller: MagnusLiveCallController<LiveCallChatBridge>, state: LiveCallCalmState, overrides: Partial<Pick<MagnusLiveCallSource, "error" | "registerChat" | "start">> = {}): MagnusLiveCallSource {
  const {
    call
  } = state;
  return {
    callChatId: state.callChatId,
    captions: call.captions,
    captionsOn: state.captionsOn,
    delegatingQuery: call.delegatingQuery,
    error: state.error,
    errorChatId: state.errorChatId,
    inputDeviceId: call.inputDeviceId,
    inputDevices: call.inputDevices,
    isMuted: call.isMuted,
    meter: controller,
    phase: call.phase,
    pushContext: controller.pushContext,
    registerChat: controller.registerChat,
    sessionId: call.sessionId,
    setCaptionsOn: controller.setCaptionsOn,
    setInputDevice: controller.setInputDevice,
    setMuted: controller.setMuted,
    setVoice: voice => {
      if (isLiveVoice(voice)) {
        controller.setVoice(voice);
      }
    },
    start: controller.start,
    startedAt: call.startedAt,
    stop: () => controller.stop(),
    voice: state.voice,
    voices: controller.voices,
    ...overrides
  };
}
export interface UseMagnusLiveCallResult {
  availability: LiveCallAvailability;
  controller: MagnusLiveCallController;
  /** Present while live calls are available, or while a call (or its
   *  error) is still on screen; absent otherwise, like `dictation: false`. */
  liveCall?: MagnusLiveCallSource;
}

/**
 * Live voice calls over `magnus.voice.liveSession` (plan 042): the browser
 * holds the WebRTC leg, the server mints the GPT-Live session, and spoken
 * requests are delegated as `magnus_chat` turns into the chat the block
 * registers. Availability is read once, on first subscribe.
 */
export function useMagnusLiveCall(t0) {
  const $ = _c(22);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    enabled: t2
  } = options;
  const enabled = t2 === undefined ? true : t2;
  const {
    client
  } = useMagnusClient();
  let t3;
  if ($[2] !== client) {
    t3 = createMagnusLiveCall({
      client
    });
    $[2] = client;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const controller = t3;
  const optionsRef = useLatest(options);
  let t4;
  let t5;
  if ($[4] !== controller || $[5] !== optionsRef) {
    t4 = () => {
      controller.setOptions({
        openChat: chatId => optionsRef.current.openChat?.(chatId),
        onChatMaterialized: chatId_0 => optionsRef.current.onChatMaterialized?.(chatId_0),
        onRecapSaved: (chatId_1, saved) => optionsRef.current.onRecapSaved?.(chatId_1, saved)
      });
    };
    t5 = [controller, optionsRef];
    $[4] = controller;
    $[5] = optionsRef;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== controller) {
    t6 = () => () => controller.dispose();
    t7 = [controller];
    $[8] = controller;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== controller || $[12] !== enabled) {
    t8 = enabled ? controller : {
      getState: controller.getState,
      subscribe: noSubscription
    };
    $[11] = controller;
    $[12] = enabled;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const watched = t8;
  const state = useLiveCallState(watched);
  const visible = enabled && (state.availability.status === "available" || state.call.phase !== "idle" || state.error !== null);
  let t9;
  if ($[14] !== controller || $[15] !== state || $[16] !== visible) {
    t9 = visible ? toLiveCallSource(controller, state) : undefined;
    $[14] = controller;
    $[15] = state;
    $[16] = visible;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const liveCall = t9;
  let t10;
  if ($[18] !== controller || $[19] !== liveCall || $[20] !== state.availability) {
    t10 = {
      availability: state.availability,
      controller,
      liveCall
    };
    $[18] = controller;
    $[19] = liveCall;
    $[20] = state.availability;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}